import { useEffect, useState } from "react";

/**
 * The design tokens a chart paints with, resolved to real colours.
 *
 * ApexCharts works colours out itself — hover shades, legend markers — and
 * cannot do that with a `var(...)`, so the tokens are read off the page. They
 * are read again whenever the root's class changes: the theme provider flips
 * `dark` there, and the status colours and the surface are stepped separately
 * for dark mode. The fallbacks are the light values, for when there is no
 * stylesheet (tests, first paint).
 */
const TOKENS = {
    series1: ["--chart-1", "#6a4fd8"],
    series2: ["--chart-2", "#0d9488"],
    series3: ["--chart-3", "#ec4a0a"],
    success: ["--status-success", "#027a48"],
    warning: ["--status-warning", "#b54708"],
    critical: ["--status-critical", "#d92d20"],
    neutral: ["--content-tertiary", "#667085"],
    surface: ["--surface-raised", "#ffffff"],
    ink: ["--content-secondary", "#475467"],
} as const;

export type ChartTokens = Record<keyof typeof TOKENS, string>;

function readTokens(): ChartTokens {
    const style = getComputedStyle(document.documentElement);
    return Object.fromEntries(
        Object.entries(TOKENS).map(([key, [name, fallback]]) => [
            key,
            style.getPropertyValue(name).trim() || fallback,
        ])
    ) as ChartTokens;
}

export function useChartTokens(): ChartTokens {
    const [tokens, setTokens] = useState(readTokens);

    useEffect(() => {
        // The provider sets the class in its own effect, which runs after this
        // component's — so watch the attribute rather than the context.
        const observer = new MutationObserver(() => setTokens(readTokens()));
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });
        return () => observer.disconnect();
    }, []);

    return tokens;
}
